import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Dialog, SelectChangeEvent } from '@mui/material'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  getActionbyProject,
  addActionsToProject,
  removeActionFromProject,
  updateAction,
} from '../../redux/actions/actionActions'
import { getProjetbyId } from '../../redux/projets/projetActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { Action } from '../../types/actionTypes'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { systemLabel } from '../../utils/systemLabel'
import { writeEntityNav } from '../../utils/entityNav'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency } from '../../utils/formatNumbers'
import i18n from '../../i18n'

import UseAlert from '../../components/common/alert'
import { ImportTasksButton } from '../action/import/ImportActionsWizard'
import SelectAction from '../action/SelectAction'
import IncyiaGantt from '../../components/common/IncyiaGantt'
import ActorAction from '../../components/containers/ActorAction'
import {
  DetailAccordion,
  DetailButton,
  DetailEntityPanel,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { priorityLabelsTranslations } from '../../utils/constants'
import { listPageChipSx } from '../../components/common/ListPageShell'
import '../../components/common/ListPageTable.css'

export default function ProjectDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { MissionId } = useParams<{ MissionId: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const locale = i18n.language
  const username = useSelector(selectUsername)
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const roles = useSelector((state: any) => state.auth.roles)

  const actionsList = useSelector((state: RootState) =>
    state.action.actionsbyproject ? state.action.actionsbyproject.actions : []
  )
  const projet = useSelector((state: RootState) => state.projet.projet)
  const loading = useSelector((state: RootState) => state.action.loading)

  const [openSelect, setOpenSelect] = useState(false)
  const [openActorPopup, setOpenActorPopup] = useState(false)
  const [ActionId, setActions] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [open, setOpen] = useState({
    info: true,
    tasks: true,
  })
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (id) {
      dispatch(getActionbyProject(id))
      dispatch(getProjetbyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!projet?._id) return
    writeEntityNav({
      projectId: projet._id,
      projectLabel: formatTitle(projet.ProjetName || ''),
      missionId: MissionId || projet.MissionId || undefined,
      missionLabel: projet.MissionName
        ? formatTitle(projet.MissionName)
        : undefined,
    })
  }, [projet, MissionId])

  const filteredActions = useMemo(() => {
    if (!actionsList) return [] as Action[]
    return actionsList.filter((action) => {
      const matchesSearch =
        !search ||
        action.ActionName?.toLowerCase().includes(search.toLowerCase()) ||
        action.Description?.toLowerCase().includes(search.toLowerCase()) ||
        action.TypeAction?.toLowerCase().includes(search.toLowerCase())
      const matchesStatus = !statusFilter || action.Status === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [actionsList, search, statusFilter])

  const canUpdate =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const canManageTasks =
    projet?.CreatedBy?.toLocaleLowerCase() === username?.toLocaleLowerCase() ||
    hasRole(roles, UserRole.ADMIN) ||
    canManageMission(roles)

  const title = formatTitle(projet?.ProjetName || '') || t('Project')
  const missionId = MissionId || projet?.MissionId || ''

  const toggle = (key: keyof typeof open) => () => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleCloseAlert = () => setAlert((a) => ({ ...a, open: false }))

  const handleDeleteClick = async (_id: string) => {
    await dispatch(
      removeActionFromProject({ actions: { actionId: _id }, _id: id || '' })
    )
    dispatch(getActionbyProject(id || ''))
  }

  const handleActionChange = (event: SelectChangeEvent<string[]>) => {
    setActions(event.target.value as string[])
  }

  const handleAddActions = async () => {
    try {
      if (ActionId.length && id) {
        await dispatch(
          addActionsToProject({ ActionId, _id: id })
        ).unwrap()
        setAlert({
          open: true,
          message: t('projectDetailsPage.alerts.updateSuccess'),
          severity: 'success',
        })
        dispatch(getActionbyProject(id))
        setOpenSelect(false)
        setOpenActorPopup(true)
      } else {
        setAlert({
          open: true,
          message: t('projectDetailsPage.alerts.fillAllFields'),
          severity: 'warning',
        })
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message: error || t('projectDetailsPage.alerts.updateError'),
        severity: 'error',
      })
    }
  }

  const statusOptions = useMemo(() => {
    const set = new Set<string>()
    ;(actionsList || []).forEach((a) => {
      if (a.Status) set.add(a.Status)
    })
    return Array.from(set)
  }, [actionsList])

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('projectDetailsPage.eyebrow', {
            defaultValue: 'Management · Projects',
          })}
          title={title}
          subtitle={t('projectDetailsPage.subtitle', {
            defaultValue:
              'Project information, tasks and schedule in one place.',
          })}
          actions={
            <>
              {canAnalyze && projet?._id ? (
                <DetailButton
                  variant="ghost"
                  onClick={() =>
                    navigate(`/project/view-analysis/${projet._id}`)
                  }
                >
                  <BarChartOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.viewAnalysis', { defaultValue: 'View Analysis' })}
                </DetailButton>
              ) : null}
              {canUpdate ? (
                <DetailButton
                  onClick={() => navigate(`/projet/${projet?._id}`)}
                >
                  <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                  {t('Update project', { defaultValue: 'Update project' })}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailAccordion
          title={t('projectDetailsPage.accordion.information')}
          open={open.info}
          onToggle={toggle('info')}
        >
          <DetailInfoGrid>
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.projectName')}
              value={title}
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.budget')}
              mono
              value={formatCurrency(projet?.Budget ?? 0, currency, {
                locale,
                useIntl: true,
              })}
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.description')}
              value={<ListPageDescription value={projet?.Description} />}
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.status')}
              value={
                projet?.Status ? (
                  <ListPagePill
                    label={t(projet.Status, { defaultValue: projet.Status })}
                    bg={
                      (STATUS_PILL[projet.Status] ?? { bg: '#EAF0F0' }).bg
                    }
                    color={
                      (STATUS_PILL[projet.Status] ?? { color: '#5F7484' })
                        .color
                    }
                  />
                ) : (
                  '—'
                )
              }
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.projectType')}
              value={
                projet?.TypeProjet
                  ? systemLabel(t, 'projectType', projet.TypeProjet)
                  : '—'
              }
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.startDate')}
              mono
              value={
                <ListPageDateCell
                  value={projet?.DateDebut}
                  locale={locale}
                />
              }
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.endDate')}
              mono
              value={
                <ListPageDateCell value={projet?.DateFin} locale={locale} />
              }
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.source')}
              value={projet?.Source || '—'}
            />
            <DetailInfoItem
              label={t('projectDetailsPage.accordion.creator')}
              value={projet?.CreatedBy || '—'}
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <DetailAccordion
          title={t('Tasks', { defaultValue: 'Tasks' })}
          count={actionsList?.length ?? 0}
          open={open.tasks}
          onToggle={toggle('tasks')}
        >
          <DetailEntityPanel
            listTitle={t('tasks_list', { defaultValue: 'Task list' })}
            searchPlaceholder={t('filterByName', {
              defaultValue: 'Filter by name',
            })}
            searchValue={search}
            onSearchChange={setSearch}
            addLabel={t('buttons.addTask', { defaultValue: 'Add New Task' })}
            onAdd={() =>
              navigate(`/mission/${missionId}/projet/${id}/addaction`)
            }
            canAdd={canManageTasks}
            extraAction={
              canManageTasks ? (
                <>
                  <ImportTasksButton
                    missionId={missionId}
                    projectId={id}
                    onImported={(created, failed) => {
                      if (id) dispatch(getActionbyProject(id))
                      setAlert({
                        open: true,
                        message: failed
                          ? t('actionImport.alerts.partial', { created, failed })
                          : t('actionImport.alerts.success', { count: created }),
                        severity: failed ? 'warning' : 'success',
                      })
                    }}
                  />
                  <DetailButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setOpenSelect(true)}
                  >
                    {t('Select Task', { defaultValue: 'Select Task' })}
                  </DetailButton>
                </>
              ) : null
            }
          >
            {statusOptions.length > 0 ? (
              <div className="incyia-status-chips">
                <button
                  type="button"
                  style={listPageChipSx(statusFilter === '')}
                  onClick={() => setStatusFilter('')}
                >
                  {t('All', { defaultValue: 'All' })}
                </button>
                {statusOptions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    style={listPageChipSx(statusFilter === s)}
                    onClick={() => setStatusFilter(s)}
                  >
                    {t(s, { defaultValue: s })}
                  </button>
                ))}
              </div>
            ) : null}

            <DetailTable<Action>
              loading={loading && !actionsList?.length}
              loadingLabel={t('loading')}
              emptyTitle={t('missionDetailsPage.empty.title', {
                defaultValue: 'Nothing here yet',
              })}
              emptyMessage={t('projectDetailsPage.empty.tasks', {
                defaultValue:
                  'No task is attached to this project yet. Add a task or select an existing one.',
              })}
              rows={filteredActions}
              rowKey={(r) => r._id as string}
              columns={[
                {
                  key: 'name',
                  header: t('task_name', { defaultValue: 'Task name' }),
                  render: (r) => (
                    <NavLink
                      to={`/actiond/${r._id}`}
                      className="incyia-name-link"
                    >
                      <ListPageDescription
                        value={
                          r.ActionName
                            ? formatTitle(r.ActionName)
                            : undefined
                        }
                        tone="primary"
                      />
                    </NavLink>
                  ),
                },
                {
                  key: 'priority',
                  header: t('Priority'),
                  render: (r) => {
                    const priority = Number(r.Priority)
                    const style = PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
                    return (
                      <ListPagePill
                        label={
                          t(`priority.${priority}`, {
                            defaultValue:
                              priorityLabelsTranslations[priority] ||
                              style.label,
                          }) || style.label
                        }
                        bg={style.bg}
                        color={style.color}
                      />
                    )
                  },
                },
                {
                  key: 'status',
                  header: t('Status'),
                  render: (r) => {
                    const style = STATUS_PILL[r.Status || ''] ?? {
                      bg: '#EAF0F0',
                      color: '#5F7484',
                    }
                    return (
                      <ListPagePill
                        label={t(r.Status || '', {
                          defaultValue: r.Status || '—',
                        })}
                        bg={style.bg}
                        color={style.color}
                      />
                    )
                  },
                },
                {
                  key: 'end',
                  header: t('End Date', { defaultValue: 'End date' }),
                  render: (r) => (
                    <ListPageDateCell
                      value={r.DateFin}
                      locale={locale}
                    />
                  ),
                },
              ]}
              actionsHeader={t('Actions', { defaultValue: 'Action' })}
              renderActions={(r) => (
                <DetailRowActions
                  roles={roles}
                  onEdit={() => navigate(`/action/${r._id}`)}
                  onDelete={() => handleDeleteClick(r._id as string)}
                  deleteItem={{
                    entityType: t('tasks', { defaultValue: 'Task' }),
                    name: r.ActionName
                      ? formatTitle(r.ActionName)
                      : undefined,
                    details: [
                      {
                        label: t('deleteConfirm.fields.mission'),
                        value: r.MissionName
                          ? formatTitle(r.MissionName)
                          : projet?.MissionName
                            ? formatTitle(projet.MissionName)
                            : '—',
                      },
                      {
                        label: t('deleteConfirm.fields.status'),
                        value: r.Status || '—',
                      },
                    ],
                  }}
                />
              )}
            />
          </DetailEntityPanel>
        </DetailAccordion>

        <div className="incyia-gantt-section" style={{ marginTop: 24, width: '100%' }}>
          <IncyiaGantt
            rows={(actionsList || [])
              .filter((a) => a.DateDebut && a.DateFin)
              .map((a) => ({
                id: a._id as string,
                name: a.ActionName || '—',
                from: new Date(a.DateDebut as Date),
                to: new Date(a.DateFin as Date),
              }))}
            onChange={async (actionId, from, to) => {
              const existing = (actionsList || []).find(
                (a) => a._id === actionId
              )
              await dispatch(
                updateAction({
                  updatedAction: {
                    ...(existing || { ActionName: '' }),
                    DateDebut: from,
                    DateFin: to,
                  },
                  _id: actionId,
                })
              )
              if (id) await dispatch(getActionbyProject(id))
            }}
          />
        </div>
      </div>

      <Dialog
        open={openSelect}
        onClose={() => setOpenSelect(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: 'incyia-modal-paper' }}
        aria-labelledby="select-task-modal"
      >
        <div className="incyia-modal-head">
          <div>
            <h3 id="select-task-modal">{t('popups.addTasksPopup.title')}</h3>
          </div>
        </div>
        <div className="incyia-modal-body">
          <SelectAction
            value={ActionId}
            onChange={handleActionChange}
            MissionId={missionId}
            onCreateNew={
              missionId && id
                ? () => {
                    setOpenSelect(false)
                    navigate(`/mission/${missionId}/projet/${id}/addaction`)
                  }
                : undefined
            }
          />
        </div>
        <div className="incyia-modal-foot">
          <DetailButton variant="ghost" onClick={() => setOpenSelect(false)}>
            {t('popups.addTasksPopup.cancelButton')}
          </DetailButton>
          <DetailButton onClick={handleAddActions}>
            {t('popups.addTasksPopup.addButton')}
          </DetailButton>
        </div>
      </Dialog>

      {openActorPopup ? (
        <ActorAction
          actionId={ActionId[0]}
          isOpen={openActorPopup}
          onClose={() => setOpenActorPopup(false)}
        />
      ) : null}
    </>
  )
}
